import classNames from 'classnames';
import { enCA, enGB, enUS, es, fr, tr, zhCN } from 'date-fns/locale';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import styled, { createGlobalStyle } from 'styled-components';
import { useUniqueName } from '../../hooks/useUniqueName';
import { COLOR, type ColorMode } from '../../styles';
import { type BareDatePickerProps } from './DatePicker.types';
import { dayToLocalDate } from './datePickerHelpers';

registerLocale('en-CA', enCA);
registerLocale('en-GB', enGB);
registerLocale('en-US', enUS);
registerLocale('es-ES', es);
registerLocale('fr-FR', fr);
registerLocale('zh-CN', zhCN);
registerLocale('tr', tr);

const BUTTON_BORDER = '2px';

function makeTestInput(testId: string) {
  const TestInput = React.forwardRef<HTMLInputElement, React.HTMLProps<HTMLInputElement>>(
    (props, ref) => <input {...props} ref={ref} data-testid={testId} />,
  );
  TestInput.displayName = `TestInput(${testId})`;
  return TestInput;
}

function makeCalendarContainer(todayBtnTestId: string) {
  const CalendarContainer: React.FC<{
    className?: string;
    children: React.ReactNode;
    showPopperArrow?: boolean;
    arrowProps?: React.HTMLAttributes<HTMLDivElement>;
  }> = ({ children, className, showPopperArrow, arrowProps = {} }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      const btn = containerRef.current?.querySelector<HTMLElement>(
        '.react-datepicker__today-button',
      );
      if (btn) btn.setAttribute('data-testid', todayBtnTestId);
    }, []);
    return (
      <div ref={containerRef} className={className}>
        {showPopperArrow && <div className="react-datepicker__triangle" {...arrowProps} />}
        {children}
      </div>
    );
  };
  CalendarContainer.displayName = `CalendarContainer(${todayBtnTestId})`;
  return CalendarContainer;
}

/**
 * This is a thin wrapper around react-datepicker that adds some styling, and which
 * converts the `Date`s that react-datepicker picks into strings.
 *
 * A regular `<input type="date">` will give you back an ISO8601 date string
 * (like "2022-10-31").  That's what we'd like to get back from the BareDatePicker
 * too.  For DateTimePicker we want an actual `Date`, but DateTimePicker needs to
 * be timezone aware, and react-datepicker isn't - it only returns dates in the
 * browser's timezone.
 *
 * To convert a react-datepicker date into a date string, we just have to format
 * the `Date` from react-datepicker as a `YYYY-MM-DD` object.  This works because
 * the user picks a date in the browser's time zone, and then we format it in
 * the browser's time zone.  (Note that we wouldn't be able to use a similar trick
 * to get a `Date` using react-datepicker as a date and time picker.  We could
 * print out the date in `YYYY-MM-DDThh:mm:ss` format and then re-parse it in
 * the desired timezone, but there would be some times that react-datepicker
 * would not be able to pick.  For example, if you were in Ottawa and wanted to
 * pick March 13 2022 at 2:30 AM in Saskatchewan, you wouldn't be able to, since
 * this time doesn't exist in Ottawa because of daylight saving time, even though
 * in does exist in Regina.)
 */
export const BareDatePicker = React.forwardRef(
  (props: BareDatePickerProps, ref: React.ForwardedRef<ReactDatePicker>) => {
    const {
      children,
      locale,
      colorMode = 'light',
      value,
      onChange,
      onSelect,
      minDate,
      maxDate,
      startDate,
      endDate,
      showTodayButton,
      showTimeInput,
      dateFormat,
      dataTestId,
      ...datePickerProps
    } = props;

    const TestInput = useMemo(
      () => (dataTestId ? makeTestInput(dataTestId) : undefined),
      [dataTestId],
    );

    const CalendarContainerComponent = useMemo(
      () => makeCalendarContainer(dataTestId ? `${dataTestId}-today-btn` : 'datepicker-today-btn'),
      [dataTestId],
    );

    const name = useUniqueName(props.name);

    const wrapperClass = classNames('date-picker', colorMode);
    const calendarClassName = classNames(props.calendarClassName ?? '', {
      'with-portal': props.portalId || props.withPortal,
    });

    const handleMonthAndYearChange = useCallback(
      (value: Date) => {
        const isRangeSelected = startDate && endDate;
        if (isRangeSelected) {
          return;
        }

        onChange?.(getDayFromDatePicker(value));
      },
      [startDate, endDate],
    );

    return (
      <Wrapper
        className={wrapperClass}
        colorMode={colorMode}
        inline={!!props.inline}
        disabled={!!props.disabled}
      >
        <ReactDatePickerGlobalStyles showTodayButton={!!showTodayButton} colorMode={colorMode} />
        <ReactDatePicker
          {...datePickerProps}
          calendarClassName={calendarClassName}
          calendarContainer={CalendarContainerComponent}
          customInput={TestInput ? <TestInput /> : undefined}
          ref={ref}
          locale={locale.localeCode}
          name={name}
          formatWeekDay={props.formatWeekDay ?? ((nameOfDay: string) => nameOfDay.slice(0, 3))}
          selected={value && typeof value === 'string' ? dayToLocalDate(value) : undefined}
          minDate={dayToLocalDate(minDate)}
          maxDate={dayToLocalDate(maxDate)}
          startDate={dayToLocalDate(startDate)}
          endDate={dayToLocalDate(endDate)}
          onChange={(value) => {
            if (value === null || value === undefined) {
              onChange?.(undefined);
            } else {
              onChange?.(getDayFromDatePicker(value));
            }
          }}
          onMonthChange={handleMonthAndYearChange}
          onYearChange={handleMonthAndYearChange}
          onSelect={(value) => {
            if (value === null || value === undefined) {
              onSelect?.(undefined);
            } else {
              onSelect?.(getDayFromDatePicker(value));
            }
          }}
          todayButton={showTodayButton ? locale.date.today : undefined}
          showTimeInput={false}
          showMonthDropdown
          showYearDropdown
          dateFormat={dateFormat || locale.date.noTimeFormatToParseFromFns}
          popperModifiers={[
            {
              name: 'offset',
              options: {
                offset: [0, 6],
              },
            },
          ]}
        />
        {children}
      </Wrapper>
    );
  },
);

interface WrapperStylesProps {
  colorMode: ColorMode;
  inline: boolean;
  disabled: boolean;
  // showTodayButton: boolean;
}

const DESELECTED_COLOR = COLOR['light-gray'];

const Wrapper = styled.span<WrapperStylesProps>`
  font-family: inherit;

  > * {
    display: inline;
  }

  &.dark {
    .react-datepicker__input-container {
      input {
        background-color: ${COLOR['dark-gray']};
        border-color: ${COLOR['dark-gray']};
        color: ${({ disabled }) => (disabled ? COLOR['light-gray-darkest'] : COLOR['white'])};
      }
    }
  }

  .react-datepicker {
    border: ${({ inline }) => (inline ? '0px solid' : `1px solid ${COLOR['primary']}`)};
    border-radius: 0.3rem;
  }
`;

interface ReactDatePickerGlobalProps {
  colorMode: ColorMode;
  showTodayButton: boolean;
}

const DATEPICKER_CLASS_PREFIX = 'react-datepicker';
const DATEPICKER_PORTAL_CLASS = `${DATEPICKER_CLASS_PREFIX}__portal`;

const ReactDatePickerGlobalStyles = createGlobalStyle<ReactDatePickerGlobalProps>`
  /* Set colors for triangle */
    .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
      top: 0;
      margin-top: -8px;
    }
    .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::before,
    .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::after {
      border-top: none;
      border-bottom-color: ${COLOR['primary']};
    }
    .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::after {
      top: 0;
    }
    .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle::before {
      top: -1px;
      border-bottom-color: ${COLOR['primary-dark']};
    }

    .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle {
      bottom: 0;
      margin-bottom: -8px;
    }
    .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::before,
    .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::after {
      border-bottom: none;
      border-top-color: ${COLOR['primary']};
    }
    .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::after {
      bottom: 0;
    }
    .react-datepicker-popper[data-placement^='top'] .react-datepicker__triangle::before {
      bottom: -1px;
      border-top-color: ${COLOR['primary-dark']};
    }

    .react-datepicker {

      &.with-portal {
        border: 1px solid ${COLOR['primary']}
      }

      .react-datepicker__navigation {
        // Because we always show the dropdowns and we hide the header, the
        // prev month/next month buttons need to be moved down slightly.
        top: 12px;
      }

      .react-datepicker__navigation-icon.react-datepicker__navigation-icon--previous::before,
      .react-datepicker__navigation-icon.react-datepicker__navigation-icon--next::before {
        border-color: ${COLOR['white']};
      }

      .react-datepicker__header {
        background-color: ${COLOR['primary']};
        border-top-left-radius: 0.25rem;
        border-top-right-radius: 0.25rem;
        color: ${COLOR['white']};
        border: 0px solid transparent;

        // Because we hide the header and show dropdowns, we need to make the whole date picker a little wider.
        width: 300px;

        .react-datepicker__day-name {
          border: ${BUTTON_BORDER} solid transparent;
        }

        .react-datepicker__current-month.react-datepicker__current-month--hasYearDropdown,
        .react-datepicker__current-month.react-datepicker__current-month--hasMonthDropdown {
          // Avoid showing the month and year a second time when it has month and year
          // dropdowns.
          display: none;
        }

        .react-datepicker__navigation.datepicker__navigation-icon::before {
          border-color: white;
        }

        .react-datepicker__current-month {
          color: ${COLOR['white']};
          margin-bottom: 8px;
        }

        .react-datepicker__day-names .react-datepicker__day-name {
          color: ${COLOR['white']};
        }

        .react-datepicker__header__dropdown {
          font-weight: normal;
          font-size: 15px;

          .react-datepicker__month-dropdown-container {
            margin-right: 7px;
          }

          .react-datepicker__month-dropdown-container,
          .react-datepicker__year-dropdown-container {
            .react-datepicker__month-read-view,
            .react-datepicker__year-read-view {
              display: flex;
              align-items: center;
              flex-direction: row-reverse;
              border-radius: 3px;
              border: 1px solid ${COLOR['white']};
              padding: 7px 15px;
              visibility: visible !important;

              .react-datepicker__month-read-view--down-arrow,
              .react-datepicker__year-read-view--down-arrow {
                width: 0;
                height: 0;
                transform: none;
                float: none;
                position: relative;
                margin-left: 16px;
                top: 4px;
                right: 0px;
                border: 6px solid transparent;
                border-top-color: ${COLOR['white']};
              }
            }

            .react-datepicker__month-dropdown,
            .react-datepicker__year-dropdown {
              border-radius: 3px;
              color: ${COLOR['black']};
              font-size: 13px;
              padding: 5px 0;

              .react-datepicker__month-option,
              .react-datepicker__year-option {
                padding: 3px 0;

                .react-datepicker__navigation--years-upcoming {
                  border-bottom-color: ${COLOR['light-gray-darkest']};
                }

                .react-datepicker__navigation--years-previous {
                  border-top-color: ${COLOR['light-gray-darkest']};
                }

                &:hover {
                  .react-datepicker__navigation--years-upcoming {
                    border-bottom-color: ${COLOR['black']};
                  }

                  .react-datepicker__navigation--years-previous {
                    border-top-color: ${COLOR['black']};
                  }
                }
              }
            }
          }
        }
      } // .react-datepicker__header

      .react-datepicker__month-container {
        float: none;
        // Leave space for the "today" button.
        padding-bottom: ${({ showTodayButton }) => (showTodayButton ? '50px' : '0')};
        background-color: ${({ colorMode }) =>
          colorMode === 'dark' ? COLOR['dark-gray-darker'] : COLOR['white']};
        border-radius: 0.3rem;

        .react-datepicker__month {
          margin: 0;
          padding: 0.4rem;
        }

        .react-datepicker__day {
          color: ${({ colorMode }) => (colorMode === 'dark' ? COLOR['white'] : COLOR['black'])};
          border: ${BUTTON_BORDER} solid transparent;
          border-radius: 0.3rem;

          &:not(.react-datepicker__day--selected, .react-datepicker__day--in-selecting-range, .react-datepicker__day--disabled):hover {
            background-color: ${({ colorMode }) =>
              colorMode === 'dark' ? COLOR['dark-gray'] : COLOR['light-gray-lighter']};
          }

          &.react-datepicker__day--disabled {
            color: ${({ colorMode }) =>
              colorMode === 'dark' ? COLOR['dark-gray-lighter'] : COLOR['light-gray']};
            cursor: default;
          }

          // When moving the cursor with the keyboard
          &.react-datepicker__day--keyboard-selected {
            background-color: ${({ colorMode }) =>
              colorMode === 'dark' ? COLOR['dark-gray-dark'] : COLOR['light-gray-lighter']};
            border-color: ${COLOR['primary']};
            color: ${COLOR['primary']};
          }

          // When picking a range of dates, this affects how dates that were chosen
          // previously but are not currently being selected appear.
          &.react-datepicker__day--in-range {
            background-color: ${DESELECTED_COLOR};
            border-color: ${DESELECTED_COLOR};
            color: ${COLOR['white']};
          }

          // Selected day, and days being selected in a range.
          &.react-datepicker__day--selected,
          &.react-datepicker__day--in-selecting-range {
            background-color: ${COLOR['primary']};
            border-color: ${COLOR['primary']};
            color: ${COLOR['white']};
          }
        }

        // When picking a range of dates, this affects how star/end dates that
        // were chosen previously but are not currently being selected appear.
        .react-datepicker__month.react-datepicker__month--selecting-range {
          .react-datepicker__day--selected:not(.react-datepicker__day--in-selecting-range) {
            background-color: ${DESELECTED_COLOR};
            border-color: ${DESELECTED_COLOR};
            color: ${COLOR['white']};
          }
        }

        .react-datepicker__day--outside-month {
          color: ${DESELECTED_COLOR};
          pointer-events: none;
        }
      }

      .react-datepicker__today-button {
        background: transparent;
        border-radius: 3px;
        border: none;
        bottom: 12px;
        color: ${COLOR['primary']};
        font-size: 13px;
        font-weight: 500;
        line-height: 20px;
        text-transform: uppercase;
        padding: 8px 15px;
        position: absolute;
        right: 17px;

        &:hover,
        &:focus,
        &:active {
          color: ${({ colorMode }) =>
            colorMode === 'dark' ? COLOR['white'] : COLOR['turquoise-darker']};
        }
      }

    }

    // override react-datepicker portal defaults
    .${DATEPICKER_PORTAL_CLASS} {

      .${DATEPICKER_CLASS_PREFIX} {
        border: none;

        .${DATEPICKER_CLASS_PREFIX}__header .${DATEPICKER_CLASS_PREFIX}__header__dropdown {
          padding: 16px 0;
        }
        
        .${DATEPICKER_CLASS_PREFIX}__navigation {
          top: 28px;
        }
      }

      .${DATEPICKER_CLASS_PREFIX}__week .${DATEPICKER_CLASS_PREFIX}__day, .${DATEPICKER_CLASS_PREFIX}__day-names .${DATEPICKER_CLASS_PREFIX}__day-name{
        width: 2rem;
        line-height: 2rem;
      }
    }
  `;

// Display Name
BareDatePicker.displayName = 'BareDatePicker';

function getDayFromDatePicker(date: Date): string;
function getDayFromDatePicker(date: Date | null): string | undefined;
function getDayFromDatePicker(date: Date | null): string | undefined {
  if (date === null || date === undefined) {
    return undefined;
  }
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(
    2,
    '0',
  )}-${`${date.getDate()}`.padStart(2, '0')}`;
}
